import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'

import { KTIcon } from '../../../../../_metronic/helpers'
import InfoHint from '../../../../common/InfoHint'
import { IsGranted, ROLE, useAuth } from '../../../auth'
import { getLedgerWalletBalance } from './core/api'
import { ILedgerWalletBalance, LedgerWalletOwnerType } from './core/types'
import LedgerDepositModal from './LedgerDepositModal'

type Status = 'loading' | 'ok' | 'not_found' | 'unavailable'

interface LedgerWalletBalanceCardProps {
  ownerType: LedgerWalletOwnerType
  id: number
}

const LedgerWalletBalanceCard: React.FC<LedgerWalletBalanceCardProps> = ({ ownerType, id }) => {
  const [status, setStatus] = useState<Status>('loading')
  const [balance, setBalance] = useState<ILedgerWalletBalance | null>(null)
  const [depositOpen, setDepositOpen] = useState(false)
  const { currentUser } = useAuth()
  const canDeposit = IsGranted([ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser)

  const fetchBalance = useCallback(async () => {
    setStatus('loading')
    try {
      const response = await getLedgerWalletBalance(ownerType, id)
      if (response.data.success && response.data.data) {
        setBalance(response.data.data)
        setStatus('ok')
      } else {
        setStatus('unavailable')
      }
    } catch (error) {
      // The global axios interceptor rejects with `error.response` (the
      // response object), so axios.isAxiosError(error) is false here — read the
      // status off the rejected object, falling back to the AxiosError shape.
      const status =
        (error as { status?: number })?.status ??
        (axios.isAxiosError(error) ? error.response?.status : undefined)
      setStatus(status === 404 ? 'not_found' : 'unavailable')
    }
  }, [ownerType, id])

  useEffect(() => {
    fetchBalance()
  }, [fetchBalance])

  return (
    <>
      <div className='card mb-7'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1 d-flex align-items-center'>
              Баланс
              <InfoHint text='Сколько мерчант может потратить прямо сейчас — с учётом уже зарезервированного под выплаты в процессе (холда)' />
            </span>
          </h3>
          {status !== 'loading' && (
            <div className='card-toolbar d-flex gap-2'>
              {ownerType === 'merchant' && (
                <Link
                  to={`/settings/merchants/${id}/ledger-history`}
                  className='btn btn-sm btn-light-primary'
                >
                  <KTIcon iconName='book' className='fs-4 me-1' />
                  История
                </Link>
              )}
              {canDeposit && (
                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={() => setDepositOpen(true)}
                >
                  <KTIcon iconName='dollar' className='fs-4 me-1' />
                  Операция над балансом
                </button>
              )}
              <button type='button' className='btn btn-sm btn-light' onClick={fetchBalance}>
                <KTIcon iconName='arrows-circle' className='fs-4 me-1' />
                Обновить
              </button>
            </div>
          )}
        </div>
        <div className='card-body py-4'>
          {status === 'loading' && (
            <div className='d-flex align-items-center text-muted fs-6'>
              <span className='spinner-border spinner-border-sm me-3' role='status' />
              Загрузка баланса...
            </div>
          )}

          {status === 'not_found' && (
            <div className='d-flex align-items-center text-muted fs-6'>
              <KTIcon iconName='information-5' className='fs-3 text-warning me-2' />
              Кошелёк в Ledger не найден
            </div>
          )}

          {status === 'unavailable' && (
            <div className='d-flex align-items-center text-muted fs-6'>
              <KTIcon iconName='information-5' className='fs-3 text-danger me-2' />
              Ledger недоступен. Попробуйте обновить позже.
            </div>
          )}

          {status === 'ok' && balance && (
            <>
              <div className='d-flex align-items-baseline flex-wrap gap-2 mb-4'>
                <span className='fw-bolder fs-2x text-gray-900'>
                  {Number(balance.availableBalance).toFixed(2)}
                </span>
                {balance.currency && (
                  <span className='fw-bold fs-4 text-gray-600'>{balance.currency}</span>
                )}
                {balance.isBlocked && (
                  <span className='badge badge-light-danger ms-2'>Кошелёк заблокирован</span>
                )}
              </div>
              <div className='row'>
                <div className='col-lg-4 mb-3 mb-lg-0'>
                  <div className='fw-bold text-gray-600 mb-1 fs-8 text-uppercase d-flex align-items-center'>
                    Кредит
                    <InfoHint text='Проведённые начисления минус проведённые списания, без учёта холда. Обычно совпадает с балансом сверху — отличается только пока есть активный холд' />
                  </div>
                  <div className='fw-semibold fs-6 text-gray-800'>
                    {Number(balance.creditBalance).toFixed(2)}
                  </div>
                </div>
                <div className='col-lg-4 mb-3 mb-lg-0'>
                  <div className='fw-bold text-gray-600 mb-1 fs-8 text-uppercase d-flex align-items-center'>
                    В холде
                    <InfoHint text='Сумма, зарезервированная под выплаты в процессе — станет доступна балансу после их завершения или отмены' />
                  </div>
                  <div className='fw-semibold fs-6 text-gray-800'>
                    {Number(balance.debitsPending).toFixed(2)}
                  </div>
                </div>
                <div className='col-lg-4'>
                  <div className='fw-bold text-gray-600 mb-1 fs-8 text-uppercase'>Кошелёк</div>
                  <span className='fs-7 text-gray-700 font-monospace'>{balance.walletId}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {canDeposit && (
        <LedgerDepositModal
          ownerType={ownerType}
          id={id}
          isOpen={depositOpen}
          onClose={() => setDepositOpen(false)}
          onSuccess={() => fetchBalance()}
          availableBalance={balance?.availableBalance}
          currency={balance?.currency}
        />
      )}
    </>
  )
}

export default LedgerWalletBalanceCard
